import React from 'react';
import { ArrowRight, BadgeCheck, Building2, Gift, ShieldCheck } from 'lucide-react';

const programs = [
  {
    icon: Building2,
    title: 'Tangibl3Net Tokenization Lab',
    text: 'The lab supports founders, asset sponsors, fiduciaries, NGOs, and issuers who need structured asset tokenization with clear records, documentation, and compliance controls.',
    bullets: ['Issuer onboarding', 'Asset documentation', 'Risk classification', 'Token setup', 'Recordkeeping', 'Compliance workflow', 'Restricted transfer controls'],
    cta: 'Apply to the Lab',
    href: '/apply',
  },
  {
    icon: ShieldCheck,
    title: 'Business Development Fee-Cover Program',
    text: 'After 90 days live on Tangibl3Net, eligible projects may apply for business development support through aligned venture participants or strategic capital partners.',
    bullets: ['Available after 90 days live on Tangibl3Net', 'Up to 50% of approved Tangibl3Net fees', 'Support period may range from 2 to 5 years', 'Consideration may include capped equity, SAFE, warrant, revenue participation, or vested token rights', 'Separate business development and investment compliance check required', 'Tangibl3Net file status does not equal investment approval'],
    cta: 'Explore Business Development',
    href: '/business-development',
  },
  {
    icon: Gift,
    title: '$10,000 Quarterly Verified Issuer Contest',
    text: 'Tangibl3Net will award $10,000 quarterly to eligible tokenization projects based on documentation quality, transparency, execution, compliance discipline, and real-world asset utility.',
    bullets: ['Available after 90 days', 'No scoring based on token price', 'No scoring based on trading volume', 'No scoring based on market capitalization', 'No scoring based on exchange listings', 'Winners selected for documentation, transparency, utility, and execution'],
    cta: 'See Contest Rules',
    href: '/program-rules#contest',
  },
];

const ruleCards = [
  ['Apply Today', 'Projects can enter the Tangibl3Net Tokenization Lab, begin onboarding, and build their asset tokenization file immediately.'],
  ['Build for 90 Days', 'Projects must maintain clean documentation, transparent communication, disciplined treasury records, and compliant marketing for at least 90 days.'],
  ['Unlock Eligibility', 'After 90 days, eligible projects may apply for business development support and the Quarterly Verified Issuer Contest.'],
];

const ProgramsFramework = () => (
  <>
    <section id="programs" className="py-24 lg:py-32 border-t border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <div className="section-label mb-4">Programs</div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-foreground">Three Programs, One Operating Framework</h2>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            Tangibl3Net and ATEED are launching a unified framework for compliant asset tokenization. Projects can apply immediately, build inside the Tangibl3Net Tokenization Lab, and become eligible for business development support or the Quarterly Verified Issuer Contest only after 90 days live on Tangibl3Net with a clean active file.
          </p>
        </div>
        <div className="grid lg:grid-cols-3 gap-5">
          {programs.map((program) => (
            <article key={program.title} className="rounded-2xl border border-border bg-card p-6 flex flex-col">
              <program.icon className="w-6 h-6 text-primary mb-4" />
              <h3 className="font-display text-xl font-bold text-foreground">{program.title}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{program.text}</p>
              <div className="mt-5 space-y-2 flex-1">
                {program.bullets.map((bullet) => (
                  <div key={bullet} className="flex gap-2 text-sm text-foreground/85"><BadgeCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" /><span>{bullet}</span></div>
                ))}
              </div>
              <a href={program.href} className="mt-6 inline-flex items-center gap-2 font-semibold text-primary">{program.cta} <ArrowRight className="w-4 h-4" /></a>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section id="ninety-day-rule" className="py-24 lg:py-32 border-t border-border bg-muted/20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-10">
          <div className="section-label mb-4">Eligibility</div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-foreground">The 90-Day Rule</h2>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            Projects may apply to Tangibl3Net immediately. However, no project may apply for the Business Development Fee-Cover Program or the $10,000 Quarterly Verified Issuer Contest until it has completed at least 90 calendar days live on Tangibl3Net with Verified File — Active status.
          </p>
        </div>
        <div className="grid lg:grid-cols-3 gap-5 mb-8">
          {ruleCards.map(([title, copy]) => (
            <article key={title} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-xl font-bold text-foreground">{title}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{copy}</p>
            </article>
          ))}
        </div>
        <div className="rounded-2xl border border-primary/30 bg-card p-5 text-center font-mono text-sm text-foreground">
          Apply → Verified File — Active → 90 Days Live → Eligibility Check → Business Development / Contest
        </div>
      </div>
    </section>
  </>
);

export default ProgramsFramework;
